import { Button, Card, CardHeader, Skeleton } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { logout } from "../../api/auth";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { pairKiosk, type KioskMode } from "../../api/kiosk";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { kioskHost } from "./host";
import type { KioskCredential } from "./store";
import s from "./kiosk.module.css";

export const setupStrings = {
  title: "Use this device as a check-in kiosk",
  subtitle: "Pair this tablet or phone to one branch. It then signs you out and shows only the check-in screen.",
  branch: "Branch",
  label: "Name for this kiosk",
  labelHint: "Shown in Settings → Devices, for example “Front desk tablet”.",
  mode: "How should members check in?",
  scannerTitle: "Scanner",
  scannerBody: "The camera reads the QR code from the member's app. Works offline.",
  wallTitle: "Wall code",
  wallBody: "The screen shows a code that changes every 30 seconds. Members scan it with their app.",
  scannerTradeoff: "A scanner kiosk keeps this gym's check-in key on the device so it can work offline: if the device is lost, remove it in Settings → Devices and the key is replaced.",
  wallTradeoff: "A wall kiosk holds only a code for itself, never the gym's check-in key.",
  submit: "Pair and sign out",
  busy: "Pairing…",
  already: "This device is already a check-in kiosk",
  alreadyBody: "Stop using it as a kiosk first if you want to pair it again.",
  alreadyAction: "Stop using as kiosk",
  storeFailed: "This device could not keep the kiosk credential. The kiosk was created: remove it in Settings → Devices and try again.",
} as const;

export function KioskSetup() {
  const { client, session, queryClient, now } = useServices();
  const ctx = useAppContext();
  const snap = useSessionSnapshot();
  const host = kioskHost();
  const existing = useQuery({ queryKey: ["kiosk", "credential"], queryFn: () => host.store.read("credential").catch(() => null), staleTime: 0, gcTime: 0, retry: false });
  const picker = snap.picker.find((p) => p.businessId === snap.businessId);
  const branches = useMemo(() => ctx.me?.branches ?? picker?.staff?.branches ?? [], [ctx.me, picker]);
  const gymName = ctx.me?.business.name ?? picker?.name ?? "";

  const [branchId, setBranchId] = useState<string>(snap.branchId ?? "");
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<KioskMode>("scanner");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const key = useRef(newIdempotencyKey());

  if (ctx.status === "loading" || existing.isPending) return <Card><div style={{ padding: 16 }} aria-busy="true"><Skeleton height={20} width="40%" /></div></Card>;
  if (!areaAllows("kiosk", ctx.has)) return <NoAccess />;
  if (existing.data) {
    return (
      <div className={s.page}>
        <Card><div className={s.form}>
          <h1>{setupStrings.already}</h1><p className={s.hint}>{setupStrings.alreadyBody}</p>
          <div className={s.bar}><Button as={Link} to="/kiosk/exit">{setupStrings.alreadyAction}</Button></div>
        </div></Card>
      </div>
    );
  }
  const chosen = branchId || branches[0]?.id || "";
  if (!snap.businessId && ctx.status === "ready") return <Navigate to="/home" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const name = label.trim();
    if (!name) { setError("Give this kiosk a name."); return; }
    if (!chosen) { setError("Choose a branch."); return; }
    setBusy(true);
    setError(null);
    try {
      const paired = await pairKiosk(client, { branchId: chosen, label: name, mode }, key.current);
      const cred: KioskCredential = {
        token: paired.token, kioskId: paired.kiosk.id, businessId: snap.businessId!, branchId: paired.kiosk.branchId,
        label: paired.kiosk.label, mode: paired.kiosk.mode, gymName, pairedAt: now(),
      };
      try {
        await host.store.write("credential", cred);
        if (paired.wall) await host.store.write("wall", { kid: paired.wall.kid, kioskSecret: paired.wall.kioskSecret, notAfter: paired.wall.notAfter });
      } catch {
        setError(setupStrings.storeFailed);
        setBusy(false);
        return; // the staff session stays: the owner still has to clean up the unused kiosk
      }
      // One credential at a time: the staff token is logged out and removed before kiosk mode renders.
      await logout(client).catch(() => undefined);
      session.signOut(null);
      queryClient.clear();
      host.assign("/kiosk");
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not pair this device. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <div className={s.head}><h1>{setupStrings.title}</h1><p>{setupStrings.subtitle}</p></div>
      <Card>
        <CardHeader title="Pair this device" />
        <form className={s.form} onSubmit={submit} noValidate>
          <div className={s.field}>
            <label htmlFor="kiosk-branch">{setupStrings.branch}</label>
            <select id="kiosk-branch" className={s.select} value={chosen} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className={s.field}>
            <label htmlFor="kiosk-label">{setupStrings.label}</label>
            <input id="kiosk-label" className={s.input} value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} autoComplete="off" />
            <span className={s.hint}>{setupStrings.labelHint}</span>
          </div>
          <fieldset className={s.field} style={{ border: 0, padding: 0 }}>
            <legend className={s.lab}>{setupStrings.mode}</legend>
            <div className={s.choices}>
              <label className={s.choice}><input type="radio" name="kiosk-mode" checked={mode === "scanner"} onChange={() => setMode("scanner")} /><span><b>{setupStrings.scannerTitle}</b><small>{setupStrings.scannerBody}</small></span></label>
              <label className={s.choice}><input type="radio" name="kiosk-mode" checked={mode === "wall"} onChange={() => setMode("wall")} /><span><b>{setupStrings.wallTitle}</b><small>{setupStrings.wallBody}</small></span></label>
            </div>
          </fieldset>
          <p className={s.callout} role="note">{mode === "scanner" ? setupStrings.scannerTradeoff : setupStrings.wallTradeoff}</p>
          {error ? <p className={s.formErr} role="alert">{error}</p> : null}
          <div className={s.bar}>
            <Button variant="primary" type="submit" busy={busy} busyLabel={setupStrings.busy}>{setupStrings.submit}</Button>
            <Button as={Link} to="/settings/devices">Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
